/**
 * Animation Runner Utility
 * 
 * Provides a reusable animation loop pattern for pan/zoom animations.
 * Handles requestAnimationFrame, progress calculation, easing, and cleanup.
 */

import { interpolate, interpolatePan, easeOutCubic } from './viewResetUtils';

/**
 * Run a pan/zoom animation with direct DOM updates
 * 
 * @param {Object} config - Animation configuration
 * @param {Object} config.startPan - Starting pan { x, y }
 * @param {Object} config.targetPan - Target pan { x, y }
 * @param {number} config.startZoom - Starting zoom level
 * @param {number} config.targetZoom - Target zoom level
 * @param {number} [config.duration=300] - Animation duration in ms
 * @param {Object} config.refs - Refs to update during animation
 * @param {Object} config.refs.panRef - Ref for current pan
 * @param {Object} config.refs.zoomRef - Ref for current zoom
 * @param {Object} config.refs.animationRef - Ref for animation frame ID (for cleanup)
 * @param {Function} config.updateCanvasTransform - DOM update function
 * @param {Function} config.setPan - React state setter for pan
 * @param {Function} [config.setZoom] - React state setter for zoom (optional if zoom doesn't change)
 * @param {Function} [config.onComplete] - Optional callback when animation completes
 * @returns {void}
 */
export function runPanZoomAnimation({
  startPan,
  targetPan,
  startZoom,
  targetZoom,
  duration = 300,
  refs,
  updateCanvasTransform,
  setPan,
  setZoom,
  onComplete,
}) {
  const { panRef, zoomRef, animationRef } = refs;
  const startTime = performance.now();
  const hasZoomChange = Math.abs(targetZoom - startZoom) >= 0.01;
  
  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    const newPan = interpolatePan(startPan, targetPan, progress);
    const newZoom = interpolate(startZoom, targetZoom, progress);
    
    // Update refs
    panRef.current = newPan;
    zoomRef.current = newZoom;
    
    // Direct DOM update for smooth animation
    updateCanvasTransform(newPan, newZoom);
    
    if (progress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      // Sync React state only at the end
      if (hasZoomChange && setZoom) {
        setZoom(targetZoom, true);
      }
      setPan(targetPan, true);
      onComplete?.();
    }
  };
  
  animationRef.current = requestAnimationFrame(animate);
}

/**
 * Run a pan-only animation (no zoom change)
 * 
 * @param {Object} config - Animation configuration
 * @param {Object} config.startPan - Starting pan { x, y }
 * @param {Object} config.targetPan - Target pan { x, y }
 * @param {number} config.currentZoom - Current zoom level (unchanged during animation)
 * @param {number} [config.duration=300] - Animation duration in ms
 * @param {Object} config.refs - Refs to update during animation
 * @param {Object} config.refs.panRef - Ref for current pan
 * @param {Object} config.refs.animationRef - Ref for animation frame ID (for cleanup)
 * @param {Function} config.updateCanvasTransform - DOM update function
 * @param {Function} config.setPan - React state setter for pan
 * @param {Function} [config.onComplete] - Optional callback when animation completes
 * @returns {void}
 */
export function runPanAnimation({
  startPan,
  targetPan,
  currentZoom,
  duration = 300,
  refs,
  updateCanvasTransform,
  setPan,
  onComplete,
}) {
  const { panRef, animationRef } = refs;
  const startTime = performance.now();
  
  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    const newPan = interpolatePan(startPan, targetPan, progress);
    
    // Update ref
    panRef.current = newPan;
    
    // Direct DOM update for smooth animation
    updateCanvasTransform(newPan, currentZoom);
    
    if (progress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      // Sync React state only at the end
      setPan(newPan, true);
      onComplete?.();
    }
  };
  
  animationRef.current = requestAnimationFrame(animate);
}

/**
 * Cancel an ongoing animation
 * 
 * @param {Object} animationRef - Ref containing animation frame ID
 */
export function cancelAnimation(animationRef) {
  if (animationRef.current) {
    cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
  }
}

/**
 * Check if pan distance is negligible (skip animation)
 * 
 * @param {Object} currentPan - Current pan { x, y }
 * @param {Object} targetPan - Target pan { x, y }
 * @param {number} [threshold=5] - Distance threshold in pixels
 * @returns {boolean} True if distance is below threshold
 */
export function isPanNegligible(currentPan, targetPan, threshold = 5) {
  const distanceX = Math.abs(currentPan.x - targetPan.x);
  const distanceY = Math.abs(currentPan.y - targetPan.y);
  return distanceX < threshold && distanceY < threshold;
}
